import React, { useEffect } from "react";
import { ArrowLeft } from "lucide-react";

const LegalLayout = ({ title, subtitle, children }) => (
  <div className="min-h-screen bg-ivory-100 text-ink-900 font-sans">
    <header className="max-w-4xl mx-auto px-6 lg:px-12 py-6 flex items-center justify-between">
      <a href="/" data-testid="legal-logo-link" className="flex items-center gap-2.5">
        <div className="relative">
          <div className="w-9 h-9 rounded-full bg-ink-900 flex items-center justify-center">
            <span className="text-gold-500 font-display font-semibold text-sm">M</span>
          </div>
          <div className="absolute -top-1 -right-1 w-3 h-3 bg-gold-500 rounded-full" />
        </div>
        <div className="leading-tight">
          <div className="font-display font-medium text-ink-900 text-[15px] tracking-tight">Moonshine</div>
          <div className="font-sans text-[11px] uppercase tracking-[0.18em] text-ink-500">Solutions</div>
        </div>
      </a>
      <a href="/" data-testid="legal-back-link" className="inline-flex items-center gap-2 text-sm text-ink-500 hover:text-gold-700 transition-colors">
        <ArrowLeft className="w-4 h-4" />
        Zur Startseite
      </a>
    </header>

    <main className="max-w-4xl mx-auto px-6 lg:px-12 pt-6 pb-24">
      <div className="text-xs uppercase tracking-[0.22em] font-bold text-gold-700 mb-4">Rechtliches</div>
      <h1 className="font-display font-medium text-ink-900 text-4xl lg:text-5xl leading-[1.1] tracking-[-0.02em] mb-3">
        {title}
      </h1>
      {subtitle && <p className="text-ink-500 mb-10">{subtitle}</p>}
      <div className="h-px w-24 bg-gold-500 mb-12" />
      <article className="prose prose-lg max-w-none text-ink-700 leading-relaxed space-y-6">
        {children}
      </article>
    </main>

    <footer className="border-t border-ink-700/10 py-8 bg-ivory-200/40">
      <div className="max-w-4xl mx-auto px-6 lg:px-12 flex flex-col sm:flex-row justify-between gap-3 text-xs text-ink-500">
        <div>© {new Date().getFullYear()} Moonshine Solutions GmbH</div>
        <nav className="flex gap-6">
          <a href="/" className="hover:text-ink-900">Startseite</a>
          <a href="/impressum" className="hover:text-ink-900">Impressum</a>
          <a href="/datenschutz" className="hover:text-ink-900">Datenschutz</a>
        </nav>
      </div>
    </footer>
  </div>
);

export const H2 = ({ children }) => (
  <h2 className="font-display text-2xl lg:text-3xl text-ink-900 font-medium mt-12 mb-4 leading-tight">
    {children}
  </h2>
);

export const H3 = ({ children }) => (
  <h3 className="font-display text-lg lg:text-xl text-ink-900 font-medium mt-8 mb-3">
    {children}
  </h3>
);

export const P = ({ children }) => (
  <p className="text-base leading-[1.7] text-ink-700">{children}</p>
);

export const Impressum = () => {
  useEffect(() => {
    const prevTitle = document.title;
    document.title = "Impressum – Moonshine Solutions";
    const desc = document.querySelector("meta[name='description']");
    const prevDesc = desc?.getAttribute("content");
    if (desc) desc.setAttribute("content", "Impressum der Moonshine Solutions GmbH, Schmitten FR, Schweiz.");
    return () => {
      document.title = prevTitle;
      if (desc && prevDesc) desc.setAttribute("content", prevDesc);
    };
  }, []);

  return (
    <LegalLayout title="Impressum" subtitle="Angaben gemäss schweizerischem Recht.">
      <H2>Betreiberin</H2>
      <P>
        <strong>Moonshine Solutions GmbH</strong><br />
        Venusweg 42<br />
        3185 Schmitten FR<br />
        Schweiz
      </P>

      <H2>Vertretung</H2>
      <P>Vertreten durch: Pascal Schwab, Geschäftsführer</P>

      <H2>Kontakt</H2>
      <P>
        E-Mail: <a href="mailto:pascal@moonshine-solutions.com" className="text-gold-700 underline hover:text-gold-600">pascal@moonshine-solutions.com</a><br />
        Web: <a href="https://moonshine-solutions.com" className="text-gold-700 underline hover:text-gold-600">moonshine-solutions.com</a>
      </P>

      <H2>Handelsregister</H2>
      <P>
        Eingetragen im Handelsregister des Kantons Freiburg.<br />
        UID-Nr.: CHE-381.126.379
      </P>

      <H2>Haftungsausschluss</H2>
      <P>
        Die Inhalte dieser Website wurden mit grösstmöglicher Sorgfalt erstellt. Für die
        Richtigkeit, Vollständigkeit und Aktualität der Inhalte kann jedoch keine
        Gewähr übernommen werden. Der Finanz- und Vorsorge-Check dient ausschliesslich
        zu informativen Zwecken und stellt keine individuelle Anlage-, Rechts- oder
        Steuerberatung dar. Investitionsentscheidungen trifft jede Person eigen­verantwortlich
        und auf eigenes Risiko.
      </P>

      <H2>Urheberrecht</H2>
      <P>
        Alle Inhalte, insbesondere Texte, Bilder, Grafiken und Logos, sind urheberrechtlich
        geschützt. Eine Weiterverwendung, Vervielfältigung oder Veröffentlichung – auch auszugsweise –
        bedarf der vorherigen schriftlichen Zustimmung der Moonshine Solutions GmbH.
      </P>

      <H2>Haftung für Links</H2>
      <P>
        Diese Website enthält Links zu externen Websites Dritter. Auf deren Inhalte hat die
        Moonshine Solutions GmbH keinen Einfluss. Für die Inhalte der verlinkten Seiten ist stets
        die jeweilige Betreiberin verantwortlich.
      </P>

      <H2>Anwendbares Recht &amp; Gerichtsstand</H2>
      <P>
        Es gilt ausschliesslich materielles Schweizer Recht unter Ausschluss von
        kollisionsrechtlichen Normen. Gerichtsstand ist Schmitten FR, sofern gesetzliche
        Vorschriften nichts anderes vorsehen.
      </P>

      <P>
        <span className="text-sm text-ink-500">Stand: {new Date().toLocaleDateString("de-CH", { year: "numeric", month: "long" })}</span>
      </P>
    </LegalLayout>
  );
};

export const Datenschutz = () => {
  useEffect(() => {
    const prevTitle = document.title;
    document.title = "Datenschutz – Moonshine Solutions";
    const desc = document.querySelector("meta[name='description']");
    const prevDesc = desc?.getAttribute("content");
    if (desc) desc.setAttribute("content", "Datenschutzerklärung der Moonshine Solutions GmbH gemäss Schweizer DSG und EU-DSGVO.");
    return () => {
      document.title = prevTitle;
      if (desc && prevDesc) desc.setAttribute("content", prevDesc);
    };
  }, []);

  return (
    <LegalLayout
      title="Datenschutzerklärung"
      subtitle="Wie wir mit deinen Daten umgehen – transparent und respektvoll."
    >
      <H2>1. Verantwortliche Stelle</H2>
      <P>
        Verantwortlich für die Datenbearbeitung auf dieser Website ist:<br /><br />
        <strong>Moonshine Solutions GmbH</strong><br />
        Venusweg 42, 3185 Schmitten FR, Schweiz<br />
        E-Mail: <a href="mailto:pascal@moonshine-solutions.com" className="text-gold-700 underline hover:text-gold-600">pascal@moonshine-solutions.com</a>
      </P>

      <H2>2. Grundsätze</H2>
      <P>
        Wir behandeln deine persönlichen Daten vertraulich und entsprechend den gesetzlichen
        Datenschutzvorschriften – insbesondere dem Schweizer Datenschutzgesetz (DSG) und, sofern
        anwendbar, der EU-Datenschutz-Grundverordnung (DSGVO). Deine Daten werden ausschliesslich
        zu den nachfolgend beschriebenen Zwecken verwendet.
      </P>

      <H2>3. Erhebung und Verarbeitung von Daten</H2>

      <H3>3.1 Beim Besuch der Website</H3>
      <P>
        Beim Aufruf dieser Website werden automatisch Informationen allgemeiner Natur erfasst
        (z. B. IP-Adresse, Browsertyp, Zeitpunkt des Zugriffs). Diese Daten lassen keinen Rückschluss
        auf deine Person zu und werden zur Sicherstellung eines störungsfreien Betriebs sowie zur
        statistischen Auswertung genutzt.
      </P>

      <H3>3.2 Bei Ausfüllen des Finanz- und Vorsorge-Checks</H3>
      <P>
        Der Vorsorge-Check wird über <a href="https://tally.so" className="text-gold-700 underline">Tally.so</a> abgewickelt.
        Die von dir eingegebenen Daten (u. a. Name, Kontaktdaten und Angaben zu deiner finanziellen
        Situation) werden zum Zweck der persönlichen Analyse und Kontaktaufnahme bearbeitet. Die
        Daten werden ausschliesslich intern verwendet und nicht an Dritte weitergegeben.
      </P>

      <H3>3.3 Bei Newsletter-Anmeldung</H3>
      <P>
        Sofern du dich für unseren Newsletter angemeldet hast, verwenden wir deine E-Mail-Adresse
        ausschliesslich für den Versand relevanter Informationen. Der Versand erfolgt über
        Resend (Resend Inc., USA). Du kannst dich jederzeit durch Antwort auf eine Mail abmelden.
      </P>

      <H2>4. Weitergabe an Dritte</H2>
      <P>
        Eine Weitergabe deiner Daten an Dritte erfolgt nur, wenn dies für die Vertragserfüllung
        erforderlich ist, du ausdrücklich eingewilligt hast oder eine gesetzliche Verpflichtung
        besteht. Eingesetzte Dienstleister (z. B. Tally, Resend, Hosting-Provider) sind vertraglich
        zur Einhaltung des Datenschutzes verpflichtet.
      </P>

      <H2>5. Cookies &amp; Tracking</H2>
      <P>
        Diese Website verwendet nur technisch notwendige Cookies sowie – zur Reichweiten- und
        Fehleranalyse – PostHog (PostHog Inc., USA). Es werden keine personenbezogenen Werbe­profile
        erstellt. Du kannst die Verwendung von Cookies in den Einstellungen deines Browsers
        deaktivieren.
      </P>

      <H2>6. Speicherdauer</H2>
      <P>
        Wir speichern deine Daten nur so lange, wie es für die genannten Zwecke erforderlich ist
        oder eine gesetzliche Aufbewahrungspflicht besteht. Danach werden die Daten gelöscht bzw.
        anonymisiert.
      </P>

      <H2>7. Deine Rechte</H2>
      <P>
        Du hast das Recht auf Auskunft, Berichtigung, Löschung, Einschränkung der Verarbeitung
        und Datenübertragbarkeit sowie auf Widerruf einer erteilten Einwilligung. Für Anfragen
        genügt eine E-Mail an <a href="mailto:pascal@moonshine-solutions.com" className="text-gold-700 underline hover:text-gold-600">pascal@moonshine-solutions.com</a>.
      </P>

      <H2>8. Datensicherheit</H2>
      <P>
        Deine Daten werden über eine verschlüsselte Verbindung (HTTPS/TLS) übertragen. Wir
        setzen zeitgemässe technische und organisatorische Massnahmen ein, um deine Daten
        gegen unberechtigten Zugriff und Missbrauch zu schützen.
      </P>

      <H2>9. Änderungen dieser Datenschutzerklärung</H2>
      <P>
        Wir behalten uns vor, diese Erklärung anzupassen, damit sie stets den aktuellen
        rechtlichen Anforderungen entspricht. Die jeweils aktuelle Fassung findest du auf
        dieser Seite.
      </P>

      <P>
        <span className="text-sm text-ink-500">Stand: {new Date().toLocaleDateString("de-CH", { year: "numeric", month: "long" })}</span>
      </P>
    </LegalLayout>
  );
};

export default Impressum;
